import { ChevronDown, ChevronRight, Search } from "lucide-react";
import * as React from "react";
import { OutcomeDot, OutcomeStripe, outcomeInk, outcomeTint } from "@/components/ui/outcome";
import { KindIcon } from "@/features/disposition-icon";
import {
  groupTerm, keepsCalling, OUTCOME_GROUPS, type OutcomeGroup, outcomeGroup, outcomeMark, outcomeMatches,
  shortLabels,
} from "@/lib/dispositions";
import type { Disposition } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * The outcomes to choose from, laid out to be found (ADR-160, ACL-508): a search box, a suggested
 * row the number keys pick, and every outcome in its coloured group. Used by the window after a
 * call (`DispositionView`) and by Set outcome (`ContactOutcomeDialog`); what a press does is
 * theirs (`onPick`).
 *
 * Keys, while nothing else on the page has the focus: 1-4 press a suggestion, a letter starts a
 * search, "/" goes to the search box. In the box, the arrows move and Enter presses the highlighted
 * outcome; Esc empties it. The window can be on screen twice (the docked phone and the Phone
 * page), so only the one drawn last and visible answers the keys.
 *
 * Wide, the groups stand side by side; narrow (the 320px phone), they are a list that folds, so the
 * search and the suggestions stay in reach.
 */
export function OutcomePicker({
  offered, withCadence, suggested, why, hidden = 0, litId, disabled, unavailable, describe, onPick,
}: {
  /** What the person may pick, in the campaign's order. */
  offered: readonly Disposition[];
  /** The stages the campaign keeps calling on a cadence (`keepsCalling`). */
  withCadence?: ReadonlySet<string>;
  /** The suggested row, at most four; empty draws none. */
  suggested: readonly Disposition[];
  /** Why these are suggested, after "Suggested ·". */
  why: string;
  /** How many outcomes the platform records itself were left out, said under the groups. */
  hidden?: number;
  /** The outcome shown pressed. */
  litId: number | null;
  disabled: boolean;
  /** A reason an outcome cannot be picked now, shown dimmed with it; null when it can. */
  unavailable?: (d: Disposition) => string | null;
  /** What an outcome does, for its hover. */
  describe?: (d: Disposition) => string | null;
  onPick: (d: Disposition) => void;
}) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);
  const narrow = useNarrow(rootRef, 520);
  const [query, setQuery] = React.useState("");
  const [cursor, setCursor] = React.useState(0);
  const [open, setOpen] = React.useState<ReadonlySet<OutcomeGroup>>(new Set());
  const searchId = React.useId();

  const free = (d: Disposition) => !disabled && !unavailable?.(d);
  const found = query.trim() ? offered.filter((d) => outcomeMatches(d, query)) : [];
  const groups = OUTCOME_GROUPS.map((group) => ({
    group,
    rows: offered.filter((d) => outcomeGroup(d) === group),
  })).filter((g) => g.rows.length > 0);
  const labels = new Map<number, string>();
  for (const g of groups) for (const [id, label] of shortLabels(g.rows)) labels.set(id, label);

  const pick = (d: Disposition | undefined) => {
    if (d && free(d)) onPick(d);
  };

  useLiveKeys(rootRef, (e) => {
    const target = e.target as HTMLElement | null;
    const inSearch = target === searchRef.current;
    // Typing anywhere else on the page (the call's form, a note) is that field's.
    const elsewhere = target && target !== document.body && !rootRef.current?.contains(target);
    if (inSearch || elsewhere || e.metaKey || e.ctrlKey || e.altKey) return;
    if (/^[1-4]$/.test(e.key) && suggested[Number(e.key) - 1]) {
      e.preventDefault();
      pick(suggested[Number(e.key) - 1]);
    } else if (e.key === "/" || /^[a-z]$/i.test(e.key)) {
      if (e.key === "/") e.preventDefault();
      searchRef.current?.focus();
    }
  });

  const row = (d: Disposition, { active = false, short = false } = {}) => {
    const reason = unavailable?.(d) ?? null;
    const lit = litId === d.id;
    const tag = d.kind === "callback" ? "time" : d.requires_note ? "note" : null;
    return (
      <button
        key={d.id}
        type="button"
        disabled={disabled || reason !== null}
        aria-pressed={lit}
        aria-current={active || undefined}
        title={reason ?? describe?.(d) ?? d.label}
        onClick={() => pick(d)}
        className={cn(
          "relative flex w-full items-center gap-1.5 rounded-md py-1 pr-1.5 pl-3 text-left text-xs font-medium text-text",
          "transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50",
          (active || lit) && "bg-surface-hover ring-1 ring-border-strong",
          lit && "text-accent-11",
        )}
      >
        <OutcomeStripe group={outcomeGroup(d)} />
        <span className="min-w-0 flex-1 truncate">{short ? (labels.get(d.id) ?? d.label) : d.label}</span>
        {tag && (
          <span
            className="shrink-0 rounded bg-surface-sunken px-1 text-[10px] leading-4 font-normal text-text-muted"
            title={tag === "time" ? "Asks when to call back" : "Asks for a note"}
          >
            {tag}
          </span>
        )}
        <KindIcon mark={outcomeMark(d, keepsCalling(d, withCadence))} />
      </button>
    );
  };

  return (
    <div ref={rootRef} className="flex flex-col gap-2.5">
      <div className="relative">
        <Search
          className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-text-subtle"
          aria-hidden
        />
        <input
          ref={searchRef}
          type="search"
          value={query}
          aria-label="Find an outcome"
          aria-describedby={searchId}
          autoComplete="off"
          onChange={(e) => {
            setQuery(e.target.value);
            setCursor(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setCursor((c) => Math.min(c + 1, Math.max(found.length - 1, 0)));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setCursor((c) => Math.max(c - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              pick(found[cursor]);
            } else if (e.key === "Escape" && query) {
              e.stopPropagation();
              setQuery("");
            }
          }}
          placeholder="Type to find an outcome"
          className={cn(
            "h-8 w-full rounded-lg border border-border bg-surface pr-24 pl-8 text-sm text-text outline-none",
            "placeholder:text-text-subtle focus:border-accent-8 focus:ring-2 focus:ring-accent-4",
            "[&::-webkit-search-cancel-button]:hidden",
          )}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-1 text-2xs text-text-subtle"
        >
          {query ? (
            <>
              <Kbd>↑↓</Kbd> <Kbd>Enter</Kbd>
            </>
          ) : (
            <>
              just type, or <Kbd>/</Kbd>
            </>
          )}
        </span>
      </div>

      {query.trim() ? (
        <div className="flex flex-col gap-1">
          <p id={searchId} className="text-2xs text-text-muted" aria-live="polite">
            {found.length === 0
              ? "No outcome matches."
              : `${found.length} ${found.length === 1 ? "match" : "matches"} · Enter records ${found[cursor]?.label ?? "the highlighted one"}`}
          </p>
          <div className="flex max-w-xl flex-col gap-0.5">
            {found.map((d, i) => (
              <div key={d.id} className="flex items-center gap-2">
                <div className="min-w-0 flex-1">{row(d, { active: i === cursor })}</div>
                {!narrow && (
                  <span className="flex w-36 shrink-0 items-center gap-1.5 text-2xs text-text-muted">
                    <OutcomeDot group={outcomeGroup(d)} />
                    {groupTerm(d.outcome_group).label}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <p id={searchId} className="sr-only">
            Type to find an outcome; the number keys press a suggestion.
          </p>
          {suggested.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <p className="text-2xs font-medium tracking-wide text-text-muted uppercase">
                Suggested · <span className="tracking-normal normal-case">{why}</span>
              </p>
              <div className={cn("grid gap-1.5", narrow ? "grid-cols-2" : "grid-cols-4")}>
                {suggested.map((d, i) => {
                  const reason = unavailable?.(d) ?? null;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      disabled={disabled || reason !== null}
                      aria-pressed={litId === d.id}
                      aria-keyshortcuts={String(i + 1)}
                      title={reason ?? describe?.(d) ?? d.label}
                      onClick={() => pick(d)}
                      style={outcomeTint(outcomeGroup(d))}
                      className={cn(
                        "flex min-w-0 items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left text-sm font-semibold text-text",
                        "transition-[filter] hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50",
                        litId === d.id && "ring-2 ring-accent-8",
                      )}
                    >
                      <Kbd strong>{i + 1}</Kbd>
                      <span className="min-w-0 flex-1 truncate">{d.label}</span>
                      <KindIcon mark={outcomeMark(d, keepsCalling(d, withCadence))} />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div
            role="group"
            aria-label="Outcomes"
            className={cn("grid gap-3", narrow ? "grid-cols-1 gap-y-1.5" : "grid-cols-6")}
          >
            {columns(groups, narrow).map((column) => (
              <div key={column.map((g) => g.group).join()} className="flex min-w-0 flex-col gap-3">
                {column.map(({ group, rows }) => {
                  const folded = narrow && !open.has(group);
                  const term = groupTerm(group);
                  const header = (
                    <>
                      <OutcomeDot group={group} className="size-2.5" />
                      <span className="flex-1 text-xs font-semibold" style={outcomeInk(group)}>
                        {term.label}
                      </span>
                      <span className="text-2xs text-text-subtle">{rows.length}</span>
                    </>
                  );
                  return (
                    <section
                      key={group}
                      aria-label={term.label}
                      className={cn("min-w-0", !narrow && "rounded-lg border border-border bg-surface p-1")}
                    >
                      {narrow ? (
                        <button
                          type="button"
                          aria-expanded={!folded}
                          onClick={() =>
                            setOpen((s) => {
                              const next = new Set(s);
                              if (next.has(group)) next.delete(group);
                              else next.add(group);
                              return next;
                            })
                          }
                          className="flex w-full items-center gap-2 rounded-md border border-border bg-surface px-1.5 py-1 text-left hover:bg-surface-hover"
                        >
                          {header}
                          {folded ? (
                            <ChevronRight className="size-3.5 text-text-subtle" aria-hidden />
                          ) : (
                            <ChevronDown className="size-3.5 text-text-subtle" aria-hidden />
                          )}
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 px-1.5 py-1" title={term.description}>
                          {header}
                        </div>
                      )}
                      {!folded && <div className="mt-0.5 flex flex-col">{rows.map((d) => row(d, { short: true }))}</div>}
                    </section>
                  );
                })}
              </div>
            ))}
          </div>
          {hidden > 0 && (
            <p className="text-2xs text-text-subtle">
              {hidden === 1 ? "One outcome" : `${hidden} outcomes`} the system records by itself{" "}
              {hidden === 1 ? "is" : "are"} not shown here.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** The groups as columns on a wide band: the short ones at the end share the last column, so six
 *  columns hold seven groups. Narrow, one column. */
function columns<G extends { group: OutcomeGroup; rows: readonly unknown[] }>(groups: G[], narrow: boolean): G[][] {
  if (narrow || groups.length <= 6) return narrow ? [groups] : groups.map((g) => [g]);
  return [...groups.slice(0, 5).map((g) => [g]), groups.slice(5)];
}

/** True while the element is narrower than `width` pixels. */
function useNarrow(ref: React.RefObject<HTMLElement | null>, width: number): boolean {
  const [narrow, setNarrow] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => setNarrow((entries[0]?.contentRect.width ?? width) < width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, width]);
  return narrow;
}

/** The pickers on screen, in the order they were drawn: only the last visible one takes keys. */
const live: HTMLElement[] = [];

function useLiveKeys(ref: React.RefObject<HTMLElement | null>, onKey: (e: KeyboardEvent) => void) {
  const handler = React.useRef(onKey);
  handler.current = onKey;
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    live.push(el);
    const listen = (e: KeyboardEvent) => {
      // Where nothing can be measured (no layout), the one drawn last.
      const visible = live.filter((p) => p.offsetParent !== null);
      const pool = visible.length > 0 ? visible : live;
      if (pool[pool.length - 1] !== el) return;
      // A dialog over the picker (a callback's time, a note) has the keys, not the picker under it.
      const dialog = (e.target as HTMLElement | null)?.closest?.('[role="dialog"]');
      if (dialog && !dialog.contains(el)) return;
      handler.current(e);
    };
    window.addEventListener("keydown", listen);
    return () => {
      window.removeEventListener("keydown", listen);
      live.splice(live.indexOf(el), 1);
    };
  }, [ref]);
}

function Kbd({ children, strong = false }: { children: React.ReactNode; strong?: boolean }) {
  return (
    <kbd
      className={cn(
        "inline-flex min-w-4 items-center justify-center rounded border px-1 font-sans text-[10px] leading-4",
        strong ? "border-border-strong bg-surface text-text" : "border-border bg-surface-sunken text-text-subtle",
      )}
    >
      {children}
    </kbd>
  );
}
